/**
 * Copyright (C) 2020 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { Box, Table, TableCell, tableCellClasses, TableRow } from '@mui/material';
import { isNil, isNumber, round } from 'lodash';
import { EMPTY_FIELD_STRING_PLACEHOLDER } from '../../../../../utils/StringUtils';
import appTheme from '../../../../common/theme/appTheme.json';
import { getLinearGradient } from '../utils/ChartUtils';

const commonCellSxStyleProps = {
  lineHeight: '16px',
  color: appTheme.primaryTextColor,
  fontFamily: appTheme.appFontFamily,
};

export interface ChartLegendTooltipEntry {
  nonDelimintedName: string;
  delimitedLastIndexDisplayName: string;
  value: number | null | undefined;
  dislayValue?: number | string | null | undefined;
  icon?: React.ReactNode | undefined;
}
export interface ChartLegendTooltipContentProps {
  entries: ChartLegendTooltipEntry[];
  totalUsage: number;
  showGraphs?: boolean;
  showIcons?: boolean;
}
const ChartLegendTooltipContent = ({ entries, totalUsage, showGraphs, showIcons }: ChartLegendTooltipContentProps) => {
  return (
    <Table
      size="small"
      sx={{
        [`& .${tableCellClasses.root}`]: {
          borderBottom: `4px solid #000000}`,
          borderTop: `4px solid #000000}`,
          color: `red`,
        },
      }}
    >
      {entries.map((entry: ChartLegendTooltipEntry) => (
        <TableRow
          key={entry.nonDelimintedName}
          sx={{
            background: showGraphs ? getLinearGradient(entry.value, totalUsage) : 'none',
          }}
        >
          {showIcons && (
            <TableCell size="small">
              <Box sx={{ ...commonCellSxStyleProps, paddingX: 'none', width: '12px' }}>{entry.icon}</Box>
            </TableCell>
          )}

          <TableCell size="small">
            <Box
              sx={{
                ...commonCellSxStyleProps,
              }}
            >
              {entry.delimitedLastIndexDisplayName}
            </Box>
          </TableCell>
          <TableCell size="small" align="right">
            <Box
              sx={{
                ...commonCellSxStyleProps,
                whiteSpace: 'nowrap',
                fontWeight: showGraphs ? 'normal' : 600,
              }}
            >
              {!isNil(entry.value) ? entry.dislayValue : EMPTY_FIELD_STRING_PLACEHOLDER}
            </Box>
          </TableCell>
          {showGraphs && (
            <TableCell size="small" align="right">
              <Box
                sx={{
                  ...commonCellSxStyleProps,
                  whiteSpace: 'nowrap',
                  fontWeight: 600,
                }}
              >
                {isNumber(entry.value) && totalUsage
                  ? (entry.value / totalUsage) * 100 < 0.01
                    ? '<0.01%'
                    : `${round((entry.value / totalUsage) * 100, 2)}%`
                  : EMPTY_FIELD_STRING_PLACEHOLDER}
              </Box>
            </TableCell>
          )}
        </TableRow>
      ))}
    </Table>
  );
};
export default ChartLegendTooltipContent;
